import { Formik, FormikConfig, FormikConsumer, FormikValues, useFormikContext } from 'formik';
import hoistNonReactStatics from 'hoist-non-react-statics';
import React from 'react';
import FormSection from './components/form-section';
import FormField, { FieldArray } from './components/FormField';
import ScrollToFieldError from './components/ScrollToFieldError';
import { FormSettingsProvider, IFormSettingsContext, useFormSettingsContext } from './Form.context';
import { FormikFormSC } from './Form.styled';
import { buildErrorsVisibility, useFormHook } from './Form.utils';

interface FormProps<ValueT extends FormikValues = FormikValues>
  extends FormikConfig<ValueT>,
    IFormSettingsContext {
  children: React.ReactNode | React.ReactNode[];
  className?: string;
  style?: React.CSSProperties;
}

const Form = <ValueT extends FormikValues = FormikValues>(
  {
    children,
    className,
    innerRef,
    layout,
    size,
    variant,
    scrollOnError = true,
    showFeedback = true,
    disabled = false,
    style,
    showLabel = true,
    ...rest
  }: FormProps<ValueT>,
  forwardedRef?: React.Ref<HTMLFormElement | null>,
) => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const formSettings = React.useMemo(
    () => ({
      layout,
      size,
      variant,
      scrollOnError,
      showFeedback,
      inForm: true,
      disabled,
      showLabel,
    }),
    [layout, size, scrollOnError, showFeedback, disabled, showLabel],
  );

  React.useImperativeHandle(forwardedRef, () => formRef.current);

  return (
    <FormSettingsProvider value={formSettings}>
      <Formik {...rest} innerRef={innerRef}>
        {typeof children === 'function' ? (
          children
        ) : (
          <FormikFormSC ref={formRef} className={className} style={style}>
            {children}
            {formSettings.scrollOnError ? <ScrollToFieldError formRef={formRef} /> : null}
          </FormikFormSC>
        )}
      </Formik>
    </FormSettingsProvider>
  );
};

Form.useForm = useFormHook;
Form.useFormContext = useFormikContext;
Form.Field = FormField;
Form.FieldArray = FieldArray;
Form.buildErrorsVisibility = buildErrorsVisibility;
Form.Custom = FormikConsumer;
Form.Section = FormSection;
Form.useFormSettings = useFormSettingsContext;
Form.gve = (e: any) => e;

export default hoistNonReactStatics(React.forwardRef(Form), Form) as typeof Form;
